'use client';

import type { ComponentType } from 'react';
import { ToggleGroup } from './toggle-group';

export type ViewToggleOption = {
  value: string;
  icon: ComponentType<{ size?: number }>;
  ariaLabel: string;
};

const VIEW_TOGGLE_ITEM_SIZE = {
  md: { minW: '72px', h: '7', px: '3', icon: 16 },
  sm: { minW: '28px', h: '6', px: '1.5', icon: 14 },
} as const;

export function ViewToggleGroup({
  value,
  options,
  onValueChange,
  size = 'md',
}: {
  value: string;
  options: readonly ViewToggleOption[];
  onValueChange: (value: string) => void;
  size?: keyof typeof VIEW_TOGGLE_ITEM_SIZE;
}) {
  const itemSize = VIEW_TOGGLE_ITEM_SIZE[size];

  return (
    <ToggleGroup.Root
      value={[value]}
      onValueChange={({ value: next }) => {
        const selected = next[0];

        if (selected && options.some((option) => option.value === selected)) {
          onValueChange(selected);
        }
      }}
      variant="ghost"
      size="sm"
      gap={0}
      p="0.5"
      borderWidth="1px"
      borderColor="border.neutral.subtle"
      borderRadius="full"
      bg="bg.neutral.subtle"
      _horizontal={{ divideX: '0' }}
    >
      {options.map((option) => {
        const Icon = option.icon;

        return (
          <ToggleGroup.Item
            key={option.value}
            value={option.value}
            aria-label={option.ariaLabel}
            minW={itemSize.minW}
            h={itemSize.h}
            px={itemSize.px}
            borderWidth="0"
            borderRadius="full"
            justifyContent="center"
            color="fg.neutral.muted"
            bg="transparent"
            shadow="none"
            boxShadow="none"
            _hover={{
              bg: 'transparent',
              color: 'fg.neutral.muted',
            }}
            _on={{
              bg: 'bg.default',
              color: 'fg.default',
              shadow: 'xs',
              _hover: {
                bg: 'bg.default',
                color: 'fg.default',
              },
            }}
          >
            <Icon size={itemSize.icon} />
          </ToggleGroup.Item>
        );
      })}
    </ToggleGroup.Root>
  );
}
